import React, { useState, useEffect } from 'react';
import permissionsApi from '../services/permissions';
import usersApi from '../services/users';
import { auth as authApi } from '../services/api';
import './Permissions.css';

const API_URL = process.env.REACT_APP_API_URL || '';

/* ═══════════════════════════════════════════════════
   SECTION FEATURE FLAGS (Fonctionnalités globales)
   ═══════════════════════════════════════════════════ */
// Drapeaux globaux : chacun expose GET/PUT <path> → { enabled }.
const FLAGS = [
  { key: 'chat', path: '/api/ai/chat/status', label: '🤖 Assistant IA (Chat CRM)',
    description: 'Interrogez les données du CRM en langage naturel — réservé aux super-admin', defaut: true },
  { key: 'fil',  path: '/api/fil/status',     label: '📰 Le Fil (réseau social d\'entreprise)',
    description: 'Onglet visible par tous les collaborateurs ; le droit de publier se donne par page ci-dessous (« Le Fil » ▸ créer)', defaut: false },
];

function FeatureFlags() {
  const [etat, setEtat] = useState({});     // key → bool | null
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    FLAGS.forEach(f => {
      fetch(`${API_URL}${f.path}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setEtat(e => ({ ...e, [f.key]: typeof d.enabled === 'boolean' ? d.enabled : f.defaut })))
        .catch(() => setEtat(e => ({ ...e, [f.key]: f.defaut })));
    });
  }, []);

  const toggle = async (f) => {
    setSaving(f.key);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}${f.path}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !etat[f.key] })
      });
      const data = await res.json();
      if (res.ok) setEtat(e => ({ ...e, [f.key]: data.enabled }));
      else alert(data.error || 'Erreur');
    } catch { alert('Erreur serveur'); }
    finally { setSaving(null); }
  };

  return (
    <div style={{
      background: 'var(--bg-muted)', border: '1px solid var(--border-default)', borderRadius: '10px',
      padding: '16px 20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px'
    }}>
      <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
        ⚙️ Fonctionnalités globales
      </div>
      {FLAGS.map(f => {
        const on = etat[f.key];
        return (
          <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{f.label}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{f.description}</div>
            </div>
            <button
              onClick={() => toggle(f)}
              disabled={saving === f.key || on === undefined}
              style={{
                padding: '7px 16px', borderRadius: '20px', border: 'none',
                cursor: saving === f.key ? 'wait' : 'pointer',
                fontWeight: '700', fontSize: '12px', whiteSpace: 'nowrap',
                background: on ? 'var(--color-success-light)' : 'var(--color-error-light)',
                color: on ? 'var(--color-success)' : 'var(--color-error)',
                transition: 'all 0.2s',
                opacity: on === undefined ? 0.5 : 1
              }}
            >
              {on === undefined ? '...' : on ? '✅ Activé' : '⛔ Désactivé'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export const PAGES = [
  { id: 'support-requests',   nom: '🏭 Assistance Constructeur', description: 'Suivi des demandes d\'assistance technique aux constructeurs (échanges, statuts, délais)' },
  { id: 'ai-search',            nom: '✨ Assistant IA',               description: 'Assistant IA interne : interrogez les données CRM et rédigez en langage naturel' },
  { id: 'bases-donnees',       nom: '🛢️ Bases de données',         description: 'Gestion des bases de données SFTP, configuration des chemins et planification des synchronisations automatiques' },
  { id: 'campagnes-medias',    nom: '📢 Campagnes médias',       description: 'Configuration des campagnes médias par concession et période (alimente les statistiques Leads Web)' },
  { id: 'config-email',        nom: '📧 Configuration Email',       description: 'État de la config Brevo, test d\'envoi et historique des emails sortants (super-admin uniquement)' },
  { id: 'leads-services',      nom: '💬 Demandes clients',       description: 'Gestion des demandes clients (tous collaborateurs)' },
  { id: 'commandes-vn',        nom: '🚢 Commandes VN',             description: 'Suivi des commandes de véhicules neufs par concession, marque et version : couverture calculée (ventes nettes et stock du DMS, commandes en cours, délai de commande), dossiers de commande en lots (ETA, n° constructeur, statut, paiement prévu), calendrier des arrivées et des décaissements. voir = consulter ; créer = nouvelles commandes et lots ; modifier = lots, prévisions, paramètres et correspondances ; supprimer = commandes et lots.' },
  { id: 'encaissements',       nom: '💶 Encaissements',            description: 'Acomptes et règlements encaissés via Adyen : saisie client + facture/bon de commande + montant, envoi sur le TPE de l\'agence ou lien de paiement (SMS / e-mail), suivi et export pour la comptabilité. voir = liste + export ; créer = encaisser ; modifier = annuler / révoquer / renvoyer. La Configuration (merchant accounts, terminaux) reste super-admin.' },
  { id: 'e-signature',         nom: '✍️ E-Signature',               description: 'Signature électronique de documents : upload PDF, lien de signature client, suivi des statuts' },
  { id: 'file-virtuelle-magasin', nom: '🛎️ File Virtuelle Magasin', description: 'File d\'attente du comptoir Pièces : enregistrement client à l\'arrivée via une borne (webapp dédiée IP-restreinte), suivi des arrivées avec temps d\'attente et Recevoir/Terminer, motifs et vendeurs configurables par concession' },
  { id: 'forfaits',            nom: '📋 Forfaits APV',            description: 'Gestion des forfaits après-vente : composition (pièces + main d\'œuvre), coûts, marges, tarifs main d\'œuvre par concession (comparatif GAR/T1/T2/T3), relevés tarifs concurrence' },
  { id: 'garantie',            nom: '📋 Garantie',                description: 'Suivi des dossiers de garantie constructeur depuis le SFTP' },
  { id: 'gestion-commerciale', nom: '📂 Gestion Commerciale',    description: 'Suivi des dossiers commerciaux VN/VO : documents, tarifs, financement, messagerie vendeur/secrétaire' },
  { id: 'parking',             nom: '🅿️ Gestion Parking',       description: 'Gestion du parking avec scan de plaques et alertes' },
  { id: 'google_ads',          nom: '📈 Google Ads',                 description: 'Tableau de bord Google Ads multi-comptes (MCC) : sélection du compte et de la période, comparaison vs période précédente, KPIs (impressions, clics, CTR, CPC, coût, conversions, coût/conv., ROAS), campagnes, mots-clés & termes de recherche, réseaux & appareils, vue consolidée de tous les comptes gérés' },
  { id: 'google_analytics',    nom: '📊 Google Analytics',           description: 'Tableau de bord Google Analytics (GA4) multi-sites : sélection du site et de la période, comparaison vs période précédente, KPIs trafic/engagement/conversions, acquisition (canaux/sources), audience (pays/appareils), pages & événements, temps réel' },
  { id: 'gmb_reviews',         nom: '🌟 Google My Business (Avis)',   description: 'Avis Google Business Profile de toutes les fiches : consultation centralisée, filtres par concession/note/statut, réponse directe (avec brouillon IA), publication vers Google' },
  { id: 'gmb_prompts',         nom: '🤖 Google My Business — Prompts IA', description: "Onglet « Prompts IA » de la page Avis Google : édition du prompt de réponse IA par fiche (ton, contexte). Complément de la page Avis Google — nécessite aussi l'accès « voir » à Google My Business (Avis) pour atteindre la page. La Configuration (OAuth, fiches) reste réservée aux super-admins." },
  { id: 'search_console',      nom: '🔎 Google Search Console',      description: 'Tableau de bord Google Search Console (SEO) multi-sites : sélection du site et de la période, comparaison vs période précédente, KPIs (clics, impressions, CTR, position moyenne), évolution, top requêtes & top pages, pays & appareils, apparence dans la recherche, opportunités SEO (requêtes « sur le pas de la porte », pages à fort potentiel mais faible CTR), vue consolidée de tous les sites' },
  { id: 'jdma',                nom: '⭐ JDMA — Avis clients',         description: 'Avis clients issus des enquêtes JDMA (APV + Ventes VN) : notes, évolution hebdo, détail par collaborateur, réponse au client par SMS ou email. L\'onglet « Analyse IA » (synthèse mensuelle par concession) reste réservé aux super-admins, quelle que soit la permission accordée ici.' },
  { id: 'kpis',               nom: '📊 KPIs',                     description: 'Suivi quotidien des indicateurs de performance par collaborateur (saisie manuelle et calculs automatiques)' },
  { id: 'ai-lab',             nom: '🧪 Labo IA',                  description: 'Laboratoire IA : briefing du matin, détection d\'anomalies, rapport hebdo — accès exclusif superadmin' },
  { id: 'labo-tools',         nom: '🧰 Labo Tools',               description: 'Hub d\'outils expérimentaux en développement — accès exclusif superadmin' },
  { id: 'fil',                 nom: '📰 Le Fil',                description: 'Publier sur le fil d\'actualité du groupe (la lecture est ouverte à tous) ; portée activité / groupe réservée aux super-admin et admin' },
  { id: 'leads',               nom: '🌐 Leads Web',             description: 'Gestion des leads provenant du web' },
  { id: 'litiges',             nom: '⚖️ Litiges',               description: 'Gestion des litiges clients' },
  { id: 'nextlane-or',         nom: '🔧 OR Atelier (Nextlane)',   description: 'Ordres de réparation multi-concessions via API Nextlane (clients, véhicules, factures, devis, lignes détaillées)' },
  { id: 'or-en-cours',         nom: '🔩 OR en cours',             description: 'Suivi des ordres de réparation actifs depuis le SFTP' },
  { id: 'stampyt',             nom: '📸 Photos VO',                description: 'Consultation des photos de véhicules d\'occasion traitées par IA (détourage fond blanc, masquage plaque)' },
  { id: 'pieces-atelier',      nom: '📦 Pièces Atelier',           description: 'Suivi des commandes de pièces passées par l\'atelier (statuts, back order, flottants…)' },
  { id: 'planning',            nom: '📅 Planning Atelier',      description: 'Planning de l\'atelier avec grille horaire' },
  { id: 'prospection-b2b',    nom: '🏢 Prospection B2B',           description: 'Recherche de prospects B2B via les données SIRENE (data.gouv.fr) : recherche par secteur, département, scoring, export CSV' },
  { id: 'prospection-pr',      nom: '🔧 Prospection PR',            description: 'Prospection des clients Garage : base clients géolocalisée (concession, code client Datacar, adresse, date de dernière visite) + carte des emplacements' },
  { id: 'reception-vip',       nom: '🏁 Réception VIP',          description: 'Gestion des dépôts véhicules clients VIP' },
  { id: 'recouvrement',        nom: '💰 Recouvrement',           description: 'Suivi des dossiers de recouvrement et relances clients' },
  { id: 'suivi_revisions',     nom: '🔔 Relances Atelier',           description: 'Suivi des passages atelier par véhicule (source SFTP Passages) pour anticiper les révisions et relancer les clients : échéance estimée (15 000 km ou 12 mois), liste de travail « À contacter » avec coordonnées, suivi de contact (voir = consulter ; modifier = synchroniser SFTP + marquer contacté)' },
  { id: 'retours_prospects',   nom: '😊 Satisfaction clients',     description: 'Questionnaires de satisfaction configurables (plusieurs formulaires par concession, questions OUI/NON, NPS 0-10, note 1-5 et texte libre, réordonnables) : ciblage des destinataires, fréquence d\'envoi, simulateur, suivi et traitement des réponses (réponse au client par mail ou SMS avec rédaction assistée). Le droit « modifier » ouvre la configuration et le traitement ; la simulation et l\'envoi réel restent super-admin' },
  { id: 'reporting-apv',      nom: '📊 Reporting APV',            description: 'Tableau de bord CA atelier (MO / Pièces / Trav. Ext.) basé sur Factures Services — accès exclusif superadmin' },
  { id: 'nextlane-reporting',  nom: '📊 Reporting Atelier (Nextlane)', description: 'Reporting financier atelier via Datacar : CA MO/Pièces, marges, taux, ventilation département/concession' },
  { id: 'reporting-financier', nom: '💰 Reporting Financier',     description: 'Suivi des performances financières par service (VN, VO, APV, PDR, ADMIN) vs Budget et N-1 — croisement de plusieurs bases de données' },
  { id: 'ringover-reporting', nom: '📞 Reporting Ringover',       description: 'Statistiques d\'appels Ringover par concession et par service (volume entrants, taux de décroché, durée, temps d\'attente)' },
  { id: 'securite-ip',        nom: '🛡️ Sécurité IP',             description: 'Configuration des restrictions d\'accès par adresse IP, par concession ou par rôle' },
  { id: 'stock-vo',           nom: '📦 Stock VO',                description: 'Suivi commercial du stock VO (Commerce VN/VO) : croise le stock courant avec la dernière offre émise par châssis pour repérer le stock dormant nécessitant une relance' },
  { id: 'atelier',             nom: '🔧 Suivi Atelier VN/VO',   description: 'Suivi des véhicules neufs et d\'occasion en atelier' },
  { id: 'mkt_audits',          nom: '🔎 Supervision — Audits',        description: 'Audits IA automatiques hebdomadaires par site (SEO, SEO local, SEA, GEO, perf, sécurité, RGPD, accessibilité, stock/annonces) : note /10 par dimension, rapport et plan d\'actions correctives générés par Claude, tendances et findings' },
  { id: 'mkt_services',        nom: '📚 Supervision — Catalogue de services', description: 'Catalogue de l\'offre des équipes marketing & digitale, organisé par pôle (Web, Marque et Contenus, Marketing, Leads Management, Digital…) : liste des expertises/prestations proposées, avec description optionnelle et statut actif/inactif. Référentiel sans donnée personnelle ; ajout/modification = droits dédiés, suppression réservée au super-admin.' },
  { id: 'mkt_clients',         nom: '👥 Supervision — Clients',       description: 'Clients du pôle marketing/digital : liste des clients actifs, rattachement des projets et des sites à un client, suivi du temps passé par client et par mois (déduit du pointage des projets)' },
  { id: 'mkt_gandi',           nom: '🌐 Supervision — Gandi (domaines)', description: 'Liste des noms de domaines gérés sur Gandi (API lecture seule) : statut, date d\'expiration, renouvellement automatique, coût de renouvellement et affectation à un client (suivi facturation), avec alertes sur les domaines qui expirent bientôt — l\'affectation et le rafraîchissement des coûts nécessitent le droit « modifier »' },
  { id: 'mkt_make',            nom: '⚙️ Supervision — Make',          description: 'Monitoring des scénarios Make.com (13 teams) : consommation d\'opérations vs quota + burn-rate, scénarios en erreur / DLQ / inactifs / stale, cadence, dernières exécutions, apps utilisées, lien profond vers Make (lecture seule)' },
  { id: 'mkt_tools',           nom: '🧰 Supervision — Outils & Accès', description: 'Registre des outils & accès de l\'équipe digitale (Brevo, Make, Notion, Gandi…) : admin du compte, responsable interne, accès & sécurité (login/SSO/2FA/gestionnaire de mots de passe), coûts et périodicité, renouvellements, statut et criticité' },
  { id: 'mkt_pointage',        nom: '⏱️ Supervision — Pointage',      description: 'Pointage du temps par projet : chronomètre live (un seul actif par utilisateur), saisie manuelle, feuille de temps et reporting par personne/projet/semaine' },
  { id: 'mkt_projects',        nom: '🗂️ Supervision — Projets',       description: 'Gestion de projet des équipes marketing & digitale : kanban projets, tâches/sous-tâches, responsable, échéances, projet relié à un site, conversion d\'un finding d\'audit en tâche' },
  { id: 'mkt_sites',           nom: '🛰️ Supervision — Sites',        description: 'Registre exhaustif des sites du groupe : gouvernance (gestionnaire interne/prestataire), comptes Google Ads / Search Console / Business Profile et leurs responsables, hébergeur/techno auto-détectée, contexte (type, zone DOM, mots-clés, marque), coûts prestataires' },
  { id: 'support-innovation',  nom: '🛟 Support & Innovation', description: 'Demandes de support et d\'évolution du CRM. Accessible à tous : chaque utilisateur ne voit que ses propres demandes ; les super-admins voient et gèrent toutes les demandes.' },
  { id: 'home',                nom: '🏠 Tableau de bord',     description: 'Page d\'accueil avec statistiques globales et alertes' },
  { id: 'takata',              nom: '🛡️ Takata',                  description: 'Gestion campagne rappel airbags Takata (dossiers, RDV, documents, stocks, budget)' },
  { id: 'tour-manager',        nom: '👁️ Tour du Manager',        description: 'Tournées de contrôle et tâches terrain avec photos' },
  { id: 'tour-vehicule',       nom: '🚗 Tour du véhicule',       description: 'Dossiers contradictoires d\'état du véhicule à la prise en charge (photos + géolocalisation + signature client + PDF récap)' },
  { id: 'vehicules-courtoisie',nom: '🚗 Véhicules Courtoisie',  description: 'Gestion de la flotte de véhicules de courtoisie' },
  { id: 'webapp',              nom: '📱 Webapp Inventaire',        description: 'Gestion des accès des utilisateurs à la webapp-inventaire : matrice modules × concessions, credentials partagés avec le CRM' },
  { id: 'workflows',           nom: '🔀 Workflows',            description: 'Création de modèles de processus (actions + décisions branchées) et suivi des exécutions en temps réel' },
];

const ROLE_LABELS = {
  'operateur':    'Opérateur',
  'admin':        'Administrateur',
  'super-admin':  'Super-admin',
  'teleoperateur':'Téléopérateur',
  'prestataire':  'Prestataire',
};

/* ═══════════════════════════════════════════════════
   VUE 1 — PAR PAGE : sélectionner une page → voir tous les utilisateurs
   ═══════════════════════════════════════════════════ */
function VueParPage() {
  const [selectedPage, setSelectedPage] = useState('home');
  const [moi, setMoi]                   = useState(null);
  const [permissions, setPermissions]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [search, setSearch]             = useState('');
  const [filterRole, setFilterRole]     = useState('');
  const [filterConcession, setFilterConcession] = useState('');

  // liste de concessions extraite des données chargées
  const concessions = [...new Map(
    permissions.filter(p => p.concession_nom).map(p => [p.concession_nom, p.concession_nom])
  ).values()].sort();

  useEffect(() => { load(); }, [selectedPage]); // eslint-disable-line
  // Qui suis-je : sert uniquement à griser le masquage sur son propre compte.
  useEffect(() => { authApi.getMe().then(r => setMoi(r.data.user || r.data)).catch(() => setMoi(null)); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await permissionsApi.getPagePermissions(selectedPage);
      setPermissions(res.data.users);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Masquage d'une page sur un compte super-administrateur — même mécanique que
  // dans la vue par utilisateur, exposée ici aussi : c'est la vue naturelle quand
  // on veut cacher UNE page à UNE personne.
  const handleToggleMasquage = async (userId, estMasquee) => {
    try {
      setSaving(true);
      if (estMasquee) await permissionsApi.demasquer(selectedPage, userId);
      else            await permissionsApi.masquer(selectedPage, userId);
      const res = await permissionsApi.getPagePermissions(selectedPage);
      setPermissions(res.data.users);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleToggle = async (userId, hasAccess) => {
    try {
      setSaving(true);
      if (hasAccess) {
        await permissionsApi.revoke(selectedPage, userId);
      } else {
        await permissionsApi.grant(selectedPage, { user_id: userId, peut_voir: true, peut_creer: true, peut_modifier: true, peut_supprimer: false });
      }
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (permissionId, updates) => {
    try {
      setSaving(true);
      await permissionsApi.update(permissionId, updates);
      load();
    } catch (e) {
      alert('Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  const filtered = permissions.filter(p => {
    if (filterRole && p.role !== filterRole) return false;
    if (filterConcession && p.concession_nom !== filterConcession) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(
        `${p.nom} ${p.prenom}`.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q)
      )) return false;
    }
    return true;
  });

  const usersWithAccess = permissions.filter(p => p.permission_id).length;

  const sorted = [...filtered].sort((a, b) => {
    const aOn = a.role === 'super-admin' || !!a.permission_id ? 0 : 1;
    const bOn = b.role === 'super-admin' || !!b.permission_id ? 0 : 1;
    return aOn - bOn;
  });

  return (
    <div>
      {/* Toolbar */}
      <div className="permissions-toolbar">
        <div className="page-selector">
          <label>Page :</label>
          <select value={selectedPage} onChange={e => setSelectedPage(e.target.value)} className="page-select">
            {PAGES.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
          </select>
          <span className="page-description">{PAGES.find(p => p.id === selectedPage)?.description}</span>
        </div>
        <div className="access-stats">
          <div className="stat-badge">
            <span className="stat-number">{usersWithAccess}</span>
            <span className="stat-label">avec accès</span>
          </div>
          <div className="stat-badge">
            <span className="stat-number">{permissions.length - usersWithAccess}</span>
            <span className="stat-label">sans accès</span>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '10px', padding: '12px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text" placeholder="🔍 Rechercher un utilisateur..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '7px 11px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
          style={{ padding: '7px 11px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: 'white', cursor: 'pointer' }}>
          <option value="">Tous les rôles</option>
          {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterConcession} onChange={e => setFilterConcession(e.target.value)}
          style={{ padding: '7px 11px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: 'white', cursor: 'pointer' }}>
          <option value="">Toutes les concessions</option>
          {concessions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(search || filterRole || filterConcession) && (
          <button onClick={() => { setSearch(''); setFilterRole(''); setFilterConcession(''); }}
            style={{ padding: '7px 12px', background: 'var(--color-error)', color: 'var(--text-inverse)', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
            Effacer
          </button>
        )}
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
          {filtered.length} / {permissions.length}
        </span>
      </div>

      {loading && <div className="loading">Chargement...</div>}

      {!loading && (
        <div className="permissions-list">
          <table>
            <thead>
              <tr>
                <th style={{ width: 50 }}>Accès</th>
                <th>Utilisateur</th>
                <th>Rôle</th>
                <th>Concession</th>
                <th style={{ width: 70, textAlign: 'center' }}>👁️ Voir</th>
                <th style={{ width: 70, textAlign: 'center' }}>➕ Créer</th>
                <th style={{ width: 70, textAlign: 'center' }}>✏️ Modif.</th>
                <th style={{ width: 70, textAlign: 'center' }}>🗑️ Suppr.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucun résultat.</td></tr>
              )}
              {sorted.map(p => {
                const isSuperAdmin = p.role === 'super-admin';
                // Sur un super-admin, une ligne avec peut_voir = false est un MASQUAGE.
                const masquee     = isSuperAdmin && !!p.permission_id && p.peut_voir === false;
                const hasAccess   = isSuperAdmin ? !masquee : !!p.permission_id;
                const soiMeme     = isSuperAdmin && p.id === moi?.id;
                return (
                  <tr key={p.id} className={hasAccess ? 'has-access' : 'no-access'}>
                    <td>
                      {isSuperAdmin
                        ? <label className="switch" title={soiMeme
                            ? 'On ne masque pas une page sur son propre compte : ce serait se couper le chemin du retour.'
                            : (masquee ? 'Page masquée — cliquer pour la rendre visible' : 'Page visible — cliquer pour la masquer')}>
                            <input type="checkbox" checked={!masquee} disabled={saving || soiMeme}
                              onChange={() => handleToggleMasquage(p.id, masquee)} />
                            <span className="slider" />
                          </label>
                        : <label className="switch">
                            <input type="checkbox" checked={hasAccess} onChange={() => handleToggle(p.id, hasAccess)} disabled={saving} />
                            <span className="slider" />
                          </label>
                      }
                    </td>
                    <td className="font-bold">
                      {p.nom} {p.prenom}
                      {isSuperAdmin && <span className="role-badge super-admin">SUPER-ADMIN</span>}
                    </td>
                    <td><span className={`role-badge ${p.role}`}>{ROLE_LABELS[p.role] || p.role}</span></td>
                    <td>{p.concession_nom || '-'}</td>
                    {isSuperAdmin ? (
                      masquee
                        ? <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>masquée sur ce compte</td>
                        : <><td style={{ textAlign: 'center' }}>✅</td><td style={{ textAlign: 'center' }}>✅</td><td style={{ textAlign: 'center' }}>✅</td><td style={{ textAlign: 'center' }}>✅</td></>
                    ) : hasAccess ? (
                      <>
                        {['peut_voir','peut_creer','peut_modifier','peut_supprimer'].map(field => (
                          <td key={field} style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={p[field]} disabled={saving}
                              onChange={e => handleUpdate(p.permission_id, { [field]: e.target.checked })} />
                          </td>
                        ))}
                      </>
                    ) : (
                      <><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td></>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   VUE 2 — PAR UTILISATEUR : sélectionner un user → voir toutes les pages
   ═══════════════════════════════════════════════════ */
function VueParUtilisateur() {
  const [users, setUsers]               = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [moi, setMoi]                   = useState(null);
  const [userPerms, setUserPerms]       = useState([]); // [{page, permission_id, peut_voir, ...}]
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [search, setSearch]             = useState('');
  const [filterRole, setFilterRole]     = useState('');
  const [filterConcession, setFilterConcession] = useState('');

  const concessions = [...new Map(
    users.filter(u => u.concession_nom).map(u => [u.concession_nom, u.concession_nom])
  ).values()].sort();

  useEffect(() => {
    usersApi.getAll()
      .then(res => {
        setUsers(res.data.users);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // Qui suis-je : sert uniquement à griser le masquage sur son propre compte.
    // Le refus fait foi côté serveur, ceci évite d'aller au clic pour rien.
    authApi.getMe().then(r => setMoi(r.data.user || r.data)).catch(() => setMoi(null));
  }, []);

  useEffect(() => {
    if (!selectedUser) { setUserPerms([]); return; }
    permissionsApi.getUserPermissions(selectedUser.id)
      .then(res => setUserPerms(res.data.permissions))
      .catch(() => setUserPerms([]));
  }, [selectedUser]);

  const getPagePerm = (pageId) =>
    userPerms.find(p => p.page === pageId) || null;

  // Masquage d'une page sur un compte super-administrateur.
  // Un super-admin passe avant la table des permissions partout dans le code :
  // on ne peut donc rien lui RETIRER, seulement lui MASQUER une page — une ligne
  // avec peut_voir = false, que le serveur lit comme un refus explicite.
  // C'est du rangement, pas du cloisonnement : l'intéressé peut se la redonner.
  const handleToggleMasquage = async (pageId, estMasquee) => {
    try {
      setSaving(true);
      if (estMasquee) await permissionsApi.demasquer(pageId, selectedUser.id);
      else            await permissionsApi.masquer(pageId, selectedUser.id);
      const res = await permissionsApi.getUserPermissions(selectedUser.id);
      setUserPerms(res.data.permissions);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleTogglePage = async (pageId, currentPerm) => {
    if (selectedUser.role === 'super-admin') return;
    try {
      setSaving(true);
      if (currentPerm) {
        await permissionsApi.revoke(pageId, selectedUser.id);
      } else {
        await permissionsApi.grant(pageId, { user_id: selectedUser.id, peut_voir: true, peut_creer: true, peut_modifier: true, peut_supprimer: false });
      }
      const res = await permissionsApi.getUserPermissions(selectedUser.id);
      setUserPerms(res.data.permissions);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleUpdatePerm = async (permissionId, pageId, field, value) => {
    try {
      setSaving(true);
      if (permissionId) {
        await permissionsApi.update(permissionId, { [field]: value });
      } else {
        // Accorder d'abord, puis mettre à jour
        await permissionsApi.grant(pageId, { user_id: selectedUser.id, peut_voir: true, peut_creer: true, peut_modifier: true, peut_supprimer: false });
        const newPerms = await permissionsApi.getUserPermissions(selectedUser.id);
        const newPerm = newPerms.data.permissions.find(p => p.page === pageId);
        if (newPerm) await permissionsApi.update(newPerm.permission_id, { [field]: value });
      }
      const res = await permissionsApi.getUserPermissions(selectedUser.id);
      setUserPerms(res.data.permissions);
    } catch (e) {
      alert('Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  const filteredUsers = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false;
    if (filterConcession && u.concession_nom !== filterConcession) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(
        `${u.nom} ${u.prenom}`.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q) ||
        (ROLE_LABELS[u.role] || '').toLowerCase().includes(q) ||
        u.concession_nom?.toLowerCase().includes(q)
      )) return false;
    }
    return true;
  });

  const pagesWithAccess = PAGES.filter(p => !!getPagePerm(p.id)).length;
  const isSuperAdmin = selectedUser?.role === 'super-admin';

  return (
    <div style={{ display: 'flex', gap: '0', alignItems: 'flex-start' }}>

      {/* ── Panneau gauche : liste des utilisateurs ── */}
      <div style={{ width: '300px', flexShrink: 0, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '0' }}>
        {/* Filtres */}
        <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input type="text" placeholder="🔍 Rechercher..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
          />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', background: 'white', cursor: 'pointer', width: '100%' }}>
            <option value="">Tous les rôles</option>
            {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={filterConcession} onChange={e => setFilterConcession(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', background: 'white', cursor: 'pointer', width: '100%' }}>
            <option value="">Toutes les concessions</option>
            {concessions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Liste utilisateurs */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</div>}
          {filteredUsers.map(u => (
            <div
              key={u.id}
              onClick={() => setSelectedUser(u)}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9',
                background: selectedUser?.id === u.id ? '#eff6ff' : 'var(--bg-surface)',
                borderLeft: selectedUser?.id === u.id ? '3px solid var(--color-info)' : '3px solid transparent',
                transition: 'background 0.1s',
              }}
            >
              <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>{u.nom} {u.prenom}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <span className={`role-badge ${u.role}`} style={{ fontSize: '10px' }}>{ROLE_LABELS[u.role] || u.role}</span>
                {u.concession_nom && <span style={{ color: 'var(--text-muted)' }}>{u.concession_nom}</span>}
              </div>
            </div>
          ))}
          {!loading && filteredUsers.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '13px' }}>Aucun utilisateur.</div>
          )}
        </div>
      </div>

      {/* ── Panneau droit : permissions de l'utilisateur sélectionné ── */}
      <div className="perms-right-panel">
        {!selectedUser && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '14px' }}>
            ← Sélectionnez un utilisateur pour gérer ses accès
          </div>
        )}

        {selectedUser && (
          <>
            {/* En-tête utilisateur */}
            <div className="perms-user-header">
              <div>
                <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{selectedUser.nom} {selectedUser.prenom}</span>
                <span style={{ marginLeft: '10px' }} className={`role-badge ${selectedUser.role}`}>{ROLE_LABELS[selectedUser.role] || selectedUser.role}</span>
                {selectedUser.concession_nom && <span style={{ marginLeft: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>{selectedUser.concession_nom}</span>}
              </div>
              {!isSuperAdmin && (
                <div style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{pagesWithAccess}</strong> / {PAGES.length} pages accessibles
                </div>
              )}
            </div>

            {/* Tableau permissions */}
            <div className="permissions-list" style={{ margin: '0' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>Accès</th>
                    <th>Page</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 400 }}>Description</th>
                    <th style={{ width: 70, textAlign: 'center' }}>👁️ Voir</th>
                    <th style={{ width: 70, textAlign: 'center' }}>➕ Créer</th>
                    <th style={{ width: 70, textAlign: 'center' }}>✏️ Modif.</th>
                    <th style={{ width: 70, textAlign: 'center' }}>🗑️ Suppr.</th>
                  </tr>
                </thead>
                <tbody>
                  {PAGES.map(page => {
                    const perm = getPagePerm(page.id);
                    // Sur un super-admin, une ligne avec peut_voir = false est un
                    // MASQUAGE : la page disparaît de son menu et son API répond 403.
                    const masquee = isSuperAdmin && !!perm && perm.peut_voir === false;
                    const hasAccess = isSuperAdmin ? !masquee : !!perm;
                    const soiMeme = isSuperAdmin && selectedUser?.id === moi?.id;
                    return (
                      <tr key={page.id} className={hasAccess ? 'has-access' : 'no-access'}>
                        <td>
                          {isSuperAdmin
                            ? <label className="switch" title={soiMeme
                                ? 'On ne masque pas une page sur son propre compte : ce serait se couper le chemin du retour.'
                                : (masquee ? 'Page masquée — cliquer pour la rendre visible' : 'Page visible — cliquer pour la masquer')}>
                                <input type="checkbox" checked={!masquee} disabled={saving || soiMeme}
                                  onChange={() => handleToggleMasquage(page.id, masquee)} />
                                <span className="slider" />
                              </label>
                            : <label className="switch">
                                <input type="checkbox" checked={!!perm} disabled={saving}
                                  onChange={() => handleTogglePage(page.id, perm)} />
                                <span className="slider" />
                              </label>
                          }
                        </td>
                        <td className="font-bold" style={{ whiteSpace: 'nowrap' }}>{page.nom}</td>
                        <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{page.description}</td>
                        {isSuperAdmin ? (
                          masquee
                            ? <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>masquée sur ce compte</td>
                            : <><td style={{ textAlign: 'center' }}>✅</td><td style={{ textAlign: 'center' }}>✅</td><td style={{ textAlign: 'center' }}>✅</td><td style={{ textAlign: 'center' }}>✅</td></>
                        ) : perm ? (
                          <>
                            {['peut_voir','peut_creer','peut_modifier','peut_supprimer'].map(field => (
                              <td key={field} style={{ textAlign: 'center' }}>
                                <input type="checkbox" checked={perm[field]} disabled={saving}
                                  onChange={e => handleUpdatePerm(perm.permission_id, page.id, field, e.target.checked)} />
                              </td>
                            ))}
                          </>
                        ) : (
                          <><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td><td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>-</td></>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   COMPOSANT PRINCIPAL avec toggle de vue
   ═══════════════════════════════════════════════════ */
function Permissions() {
  const [viewMode, setViewMode] = useState('page'); // 'page' | 'user'

  return (
    <div className="permissions-container">
      {/* Fonctionnalités globales */}
      <FeatureFlags />

      {/* Toggle vue */}
      <div className="page-tabs">
        <button
          className={viewMode === 'page' ? 'active' : ''}
          onClick={() => setViewMode('page')}
        >
          📄 Vue par page
        </button>
        <button
          className={viewMode === 'user' ? 'active' : ''}
          onClick={() => setViewMode('user')}
        >
          👤 Vue par utilisateur
        </button>
      </div>

      {viewMode === 'page' && <VueParPage />}
      {viewMode === 'user' && <VueParUtilisateur />}
    </div>
  );
}

export default Permissions;
